import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #activeIndex = 0;
  /** @type {boolean} */
  #isZoomed = false;
  /** @type {number} */
  #zoomScale = 2.5;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Shows a single image at the given index (desktop only).
   * Hides all other media items and updates thumbnails.
   * @param {number} index - The index of the image to show.
   */
  #showSingleImage(index) {
    const { media, thumbnails } = this.refs;
    if (!media || media.length === 0) return;

    // Clamp index
    if (index < 0) index = 0;
    if (index >= media.length) index = media.length - 1;

    this.#activeIndex = index;
    this.#resetZoom();

    // Hide all except active
    media.forEach((item, i) => {
      item.hidden = i !== index;
    });

    // Update thumbnail selection
    if (thumbnails) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-current', `${i === index}`);
      });

      // Scroll thumbnail into view
      const targetThumbnail = thumbnails.children[index];
      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });
      }
    }

    // Load high-res
    this.loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Zooms into a clicked point on the image (desktop Stage 2).
   * @param {HTMLElement} mediaItem - The media container <li>.
   * @param {MouseEvent} event - The click event.
   */
  #zoomToPoint(mediaItem, event) {
    const image = mediaItem.querySelector('img.product-media__image');
    if (!image) return;

    const rect = image.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    const imageCenterX = rect.width / 2;
    const imageCenterY = rect.height / 2;
    const scale = this.#zoomScale;

    const dx = clickX - imageCenterX;
    const dy = clickY - imageCenterY;
    let tx = -dx * (scale - 1);
    let ty = -dy * (scale - 1);

    // Clamp so we don't show empty space
    const maxTx = (rect.width * scale - rect.width) / 2;
    const maxTy = (rect.height * scale - rect.height) / 2;
    tx = Math.max(-maxTx, Math.min(maxTx, tx));
    ty = Math.max(-maxTy, Math.min(maxTy, ty));

    image.style.transformOrigin = 'center center';
    image.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;

    mediaItem.setAttribute('data-zoomed', '');
    this.#isZoomed = true;
  }

  /**
   * Resets zoom on all media items (removes transforms + data-zoomed).
   */
  #resetZoom() {
    const { media } = this.refs;
    this.#isZoomed = false;
    media.forEach((item) => {
      item.removeAttribute('data-zoomed');
      const img = item.querySelector('img.product-media__image');
      if (img) {
        img.style.transform = '';
        img.style.transformOrigin = '';
      }
    });
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const isDesktop = !isMobileBreakpoint();
    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      if (isDesktop) {
        this.#showSingleImage(index);
      } else {
        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'instant' });
        }
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    if (!isDesktop) {
      this.selectThumbnail(index, { behavior: 'instant' });
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // Preserve any existing transforms (zoom state) during high-res swap
    if (image.style.transform) {
      newImage.style.transform = image.style.transform;
    }
    if (image.style.transformOrigin) {
      newImage.style.transformOrigin = image.style.transformOrigin;
    }

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    // Skip scroll handling on desktop (single-image mode)
    if (!isMobileBreakpoint()) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    const isDesktop = !isMobileBreakpoint();

    if (isDesktop) {
      this.#resetZoom();
    }

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      if (isDesktop) {
        media.forEach((li) => { li.hidden = false; });
      }
      return this.closeDialog();
    }

    let mostVisibleElement;
    let activeIndex;

    if (isDesktop) {
      activeIndex = this.#activeIndex;
      mostVisibleElement = media[activeIndex];
    } else {
      mostVisibleElement = await getMostVisibleElement(media);
      activeIndex = media.indexOf(mostVisibleElement);
    }

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      if (isDesktop) {
        media.forEach((li) => { li.hidden = false; });
      }
      return this.closeDialog();
    }

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    // Unhide all before transition on desktop
    if (isDesktop) {
      media.forEach((li) => { li.hidden = false; });
    }

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles image click for two-stage zoom on desktop.
   * Mobile: closes the dialog (original behavior).
   * Desktop Stage 1: zooms to point. Stage 2: resets zoom.
   *
   * @param {number} index - The index of the image.
   * @param {MouseEvent} event - The click event.
   */
  handleImageClick(index, event) {
    if (isMobileBreakpoint()) {
      this.close();
      return;
    }

    const { media } = this.refs;
    const mediaItem = media[index];
    if (!mediaItem) return;

    // Only act on image containers
    if (!mediaItem.classList.contains('product-media-container--image')) return;

    if (this.#isZoomed) {
      this.#resetZoom();
    } else {
      this.#zoomToPoint(mediaItem, event);
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   * Arrow keys navigate between images on desktop.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (!isMobileBreakpoint()) {
      const { media } = this.refs;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = (this.#activeIndex + 1) % media.length;
        this.#showSingleImage(nextIndex);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = (this.#activeIndex - 1 + media.length) % media.length;
        this.#showSingleImage(prevIndex);
      }
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    if (!isMobileBreakpoint()) {
      this.#showSingleImage(index);
      return;
    }
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
